/**
 * OrganizationDetails — workspace setup screen (step 2 of 2 in signup).
 *
 * Reads the user's session and signup data from sessionStorage (set in SignUp).
 * On submit it:
 *   1. Creates a tenant record in the `tenants` table.
 *   2. Links the user's profile row to the new tenant.
 *   3. Saves CRM/lead-capture data to `organization_demo_details`.
 * On completion, a full-page reload to /dashboard ensures AuthContext.init()
 * re-runs and picks up the stored session before ProtectedRoute checks.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Organizationdetails.css';
import logo from '../../components/images/logo.png';
import characterImage from '../../components/images/onboarding_image.png';

const OrganizationDetails = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    organizationName: '',
    organizationSize: '',
    country: '',
    phoneNumber: '',
    currentTalentSystem: '',
    talentManagementNeeds: '',
    requestContact: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Guard: redirect back to signup if the user navigated here directly
  useEffect(() => {
    const signupData = sessionStorage.getItem('signupData');
    const sessionData = sessionStorage.getItem('supabase-auth-token');
    if (!signupData && !sessionData) {
      navigate('/sign-up', { replace: true });
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const signupDataStr = sessionStorage.getItem('signupData');
      const sessionDataStr = sessionStorage.getItem('supabase-auth-token');

      if (!signupDataStr || !sessionDataStr) {
        setError('Session expired. Please sign up again.');
        setTimeout(() => navigate('/sign-up'), 2000);
        setIsLoading(false);
        return;
      }

      const signupData = JSON.parse(signupDataStr);
      const sessionData = JSON.parse(sessionDataStr);
      const userId = signupData.userId || sessionData.user?.id;
      const userEmail = signupData.email || sessionData.user?.email;

      if (!userId) {
        setError('User ID not found. Please sign up again.');
        setTimeout(() => navigate('/sign-up'), 2000);
        setIsLoading(false);
        return;
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const accessToken = sessionData.access_token;

      // ── Steps 1+2: Create tenant and link profile atomically via RPC ───
      const rpcResponse = await fetch(`${supabaseUrl}/rest/v1/rpc/create_tenant_and_link_profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          p_organization_name: formData.organizationName,
          p_organization_size: formData.organizationSize,
          p_country: formData.country,
          p_phone_number: formData.phoneNumber,
          p_first_name: signupData.firstName || null,
          p_last_name: signupData.lastName || null
        })
      });

      if (!rpcResponse.ok) {
        const errorData = await rpcResponse.json();
        console.error('Tenant creation failed:', errorData);
        const detail = errorData?.message || errorData?.hint || errorData?.code || rpcResponse.status;
        setError(`Failed to create your workspace: ${detail}. Please try again.`);
        setIsLoading(false);
        return;
      }

      // ── Step 2b: Assign CHRO role to the account owner ────────────────
      await fetch(`${supabaseUrl}/rest/v1/profiles?id=eq.${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({ role: 'chro' })
      });

      // ── Step 3: Save CRM / lead-capture data ──────────────────────────
      const orgResponse = await fetch(`${supabaseUrl}/rest/v1/organization_demo_details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${accessToken}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          user_id: userId,
          email: userEmail,
          organization_name: formData.organizationName,
          organization_size: formData.organizationSize,
          country: formData.country,
          phone_number: formData.phoneNumber,
          current_talent_system: formData.currentTalentSystem,
          talent_management_needs: formData.talentManagementNeeds,
          request_contact: formData.requestContact
        })
      });

      if (!orgResponse.ok) {
        console.warn('CRM lead data save failed (non-critical)');
      }

      sessionStorage.setItem('organizationData', JSON.stringify(formData));
      setIsLoading(false);

      // Force a full-page reload to /dashboard so AuthContext.init() re-runs
      // and picks up the session from sessionStorage before ProtectedRoute checks.
      // React Router's navigate() reuses the live context (user = null at this
      // point) and ProtectedRoute would redirect to /login.
      window.location.href = '/dashboard';

    } catch (err) {
      console.error('Organization details submission error:', err.message);
      if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(`Failed to save details: ${err.message}`);
      }
      setIsLoading(false);
    }
  };

  const organizationSizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1001-5000 employees',
    '5000+ employees'
  ];

  const talentSystems = [
    'Workday',
    'SAP SuccessFactors',
    'Oracle HCM Cloud',
    'ADP Workforce Now',
    'BambooHR',
    'Namely',
    'UltiPro (UKG Pro)',
    'Cornerstone OnDemand',
    'iCIMS',
    'Greenhouse',
    'Lever',
    'SmartRecruiters',
    'Proprietary System',
    'Other'
  ];

  return (
    <div className="org-details-container">
      <div className="org-details-left">
        <div className="org-details-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="TalentScope360" />
        </div>

        <div className="org-details-content">
          <h1 className="org-details-title">Workspace Setup</h1>
          <p className="org-details-subtitle">Let's get you started...</p>

          <form className="org-details-form" onSubmit={handleSubmit}>
            {error && (
              <div style={{ 
                color: '#dc2626', 
                backgroundColor: '#fee2e2', 
                padding: '12px', 
                borderRadius: '6px', 
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            <input
              type="text"
              name="organizationName"
              placeholder="Organization Name"
              className="org-details-input"
              value={formData.organizationName}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <select
              name="organizationSize"
              className="org-details-input org-details-select"
              value={formData.organizationSize}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            >
              <option value="">Select Organization Size</option>
              {organizationSizes.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>

            <input
              type="text"
              name="country"
              placeholder="Country"
              className="org-details-input"
              value={formData.country}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <input
              type="tel"
              name="phoneNumber"
              placeholder="Phone Number"
              className="org-details-input"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <select
              name="currentTalentSystem"
              className="org-details-input org-details-select"
              value={formData.currentTalentSystem}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            >
              <option value="">What is your current Talent management system?</option>
              {talentSystems.map((system) => (
                <option key={system} value={system}>{system}</option>
              ))}
            </select>

            <textarea
              name="talentManagementNeeds"
              placeholder="What are your current talent management needs?"
              className="org-details-input org-details-textarea"
              value={formData.talentManagementNeeds}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              rows="4"
            />

            <div className="org-details-checkbox-container">
              <input
                type="checkbox"
                id="requestContact"
                name="requestContact"
                checked={formData.requestContact}
                onChange={handleInputChange}
                disabled={isLoading}
                className="org-details-checkbox"
              />
              <label htmlFor="requestContact" className="org-details-checkbox-label">
                I would like someone to reach out to me regarding the talent management solution after the demo
              </label>
            </div>

            <button 
              type="submit" 
              className="org-details-button"
              disabled={isLoading}
            >
              {isLoading ? 'Setting up...' : 'Proceed to Dashboard'}
            </button>
          </form>
        </div>
      </div>

      <div className="org-details-right">
        <div className="org-details-right-content">
          <div className="org-details-character">
            <img src={characterImage} alt="Character" />
          </div>
          <h2 className="org-details-right-title">Streamline your Talent Management.</h2>
          <p className="org-details-right-text">
            Transform your workforce into a competitive advantage with TalentScope360.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDetails;